import { useEffect } from 'react';
import { Form, Input, Checkbox } from 'antd';
import { SyncIconButton } from './SyncIconButton';
import { DiffCaption, DiffBanner } from './DiffWarning';
import { FloatingField } from './FloatingField';
import {
  buyerCccdRules,
  CCCD_PATTERN,
  invoiceEmailRules,
  invoiceTaxIdRules,
  requiredRule,
  TAX_CODE_PATTERN,
} from './customerFormSchema';
import type { UserType } from '@/pages/POS/types';

interface Props {
  form: import('antd').FormInstance;
  userType: UserType;
}

function joinDeliveryAddress(form: import('antd').FormInstance) {
  const address = form.getFieldValue('customer_address');
  const wardName = form.getFieldValue('_wardName');
  const cityName = form.getFieldValue('_cityName');
  return [address, wardName, cityName].filter(Boolean).join(', ');
}

export function InvoiceSection({ form, userType }: Props) {
  // Set a target field and validate it so any rule violation (e.g. invalid
  // email format inherited from source) surfaces on the target immediately.
  // Without this, sync icons silently propagate bad data.
  const setAndValidate = (targetName: string, value: unknown) => {
    form.setFieldValue(targetName, value);
    form.validateFields([targetName]).catch(() => undefined);
  };

  const sameAsDelivery = Form.useWatch('invoice_same_as_delivery', form);
  const customerName = Form.useWatch('customer_name', form);
  const customerEmail = Form.useWatch('customer_email', form);
  const citizenId = Form.useWatch('citizen_id', form);
  const customerAddress = Form.useWatch('customer_address', form);
  const wardName = Form.useWatch('_wardName', form);
  const cityName = Form.useWatch('_cityName', form);

  const invoiceName = Form.useWatch('invoice_name', form);
  const invoiceTaxId = Form.useWatch('invoice_tax_id', form);
  const invoiceAddress = Form.useWatch('invoice_address', form);
  const invoiceEmail = Form.useWatch('invoice_email', form);

  const deliveryFullAddress = [customerAddress, wardName, cityName].filter(Boolean).join(', ');

  useEffect(() => {
    if (sameAsDelivery && userType === 'personal') {
      const COPIED_FIELDS = [
        'invoice_name',
        'invoice_tax_id',
        'invoice_address',
        'invoice_email',
        'buyer_name',
        'buyer_cccd',
      ];
      form.setFieldsValue({
        invoice_name: customerName ?? '',
        invoice_tax_id: citizenId ?? '',
        invoice_address: deliveryFullAddress,
        invoice_email: customerEmail ?? '',
        buyer_name: customerName ?? '',
        buyer_cccd: citizenId ?? '',
      });
      // Validate ngay sau khi copy để error/required hiển thị tức thì.
      // validateFields trả Promise reject khi có lỗi — antd tự render lỗi vào
      // Form.Item, ta chỉ cần swallow để tránh unhandled rejection warning.
      form.validateFields(COPIED_FIELDS).catch(() => undefined);
    }
  }, [
    sameAsDelivery,
    customerName,
    citizenId,
    deliveryFullAddress,
    customerEmail,
    userType,
    form,
  ]);

  const isPersonal = userType === 'personal';
  // Diff captions/banner only make sense when the user has claimed "same as
  // delivery". Otherwise the screen warns about a difference the user never
  // promised wouldn't exist.
  const gateDiff = isPersonal && !!sameAsDelivery;

  const showDiffName =
    gateDiff && invoiceName && customerName && invoiceName !== customerName;
  // Only show "Khác với CCCD khách hàng" once the value is a valid CCCD/MST —
  // otherwise it stacks on top of the format error and becomes unreadable.
  const isInvoiceTaxIdValid =
    !!invoiceTaxId && (CCCD_PATTERN.test(invoiceTaxId) || TAX_CODE_PATTERN.test(invoiceTaxId));
  const showDiffTaxId =
    gateDiff && isInvoiceTaxIdValid && citizenId && invoiceTaxId !== citizenId;
  const showDiffAddress =
    gateDiff &&
    invoiceAddress &&
    deliveryFullAddress &&
    invoiceAddress !== deliveryFullAddress;
  const showDiffEmail =
    gateDiff && invoiceEmail && customerEmail && invoiceEmail !== customerEmail;

  const showBanner =
    showDiffName || showDiffTaxId || showDiffAddress || showDiffEmail;

  return (
    <>
      {isPersonal && (
        <Form.Item
          name="invoice_same_as_delivery"
          valuePropName="checked"
          style={{ marginBottom: 8 }}
        >
          <Checkbox>Giống thông tin giao hàng</Checkbox>
        </Form.Item>
      )}

      <FloatingField
        label={isPersonal ? 'Tên khách hàng' : 'Tên công ty xuất hoá đơn'}
        name="invoice_name"
        rules={[requiredRule('tên trên hoá đơn')]}
        required
      >
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ họ tên khách hàng"
              onClick={() =>
                setAndValidate('invoice_name', form.getFieldValue('customer_name') ?? '')
              }
            />
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffName} message="Khác với họ tên khách hàng" />

      <FloatingField
        label={isPersonal ? 'CCCD/MST' : 'Mã số thuế'}
        name="invoice_tax_id"
        rules={invoiceTaxIdRules}
        required
      >
        <Input
          maxLength={14}
          suffix={
            isPersonal ? (
              <SyncIconButton
                tooltip="Đồng bộ từ CCCD khách hàng"
                onClick={() =>
                  setAndValidate('invoice_tax_id', form.getFieldValue('citizen_id') ?? '')
                }
              />
            ) : null
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffTaxId} message="Khác với CCCD khách hàng" />

      <FloatingField
        label="Địa chỉ xuất hoá đơn"
        name="invoice_address"
        rules={[requiredRule('địa chỉ xuất hoá đơn')]}
        required
        suffix={
          <SyncIconButton
            tooltip="Đồng bộ từ địa chỉ giao hàng"
            onClick={() => setAndValidate('invoice_address', joinDeliveryAddress(form))}
          />
        }
      >
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </FloatingField>
      <DiffCaption show={!!showDiffAddress} message="Khác với địa chỉ giao hàng" />

      <FloatingField label="Email nhận hoá đơn" name="invoice_email" rules={invoiceEmailRules}>
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ email khách hàng"
              onClick={() =>
                setAndValidate('invoice_email', form.getFieldValue('customer_email') ?? '')
              }
            />
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffEmail} message="Khác với email khách hàng" />

      <FloatingField label="Tên người mua" name="buyer_name">
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ họ tên khách hàng"
              onClick={() =>
                setAndValidate('buyer_name', form.getFieldValue('customer_name') ?? '')
              }
            />
          }
        />
      </FloatingField>

      <FloatingField
        label="CCCD người mua"
        name="buyer_cccd"
        rules={buyerCccdRules}
        required
      >
        <Input
          maxLength={12}
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ CCCD khách hàng"
              onClick={() =>
                setAndValidate('buyer_cccd', form.getFieldValue('citizen_id') ?? '')
              }
            />
          }
        />
      </FloatingField>

      <Form.Item name="misa_account_object_id" hidden>
        <Input />
      </Form.Item>

      <DiffBanner show={!!showBanner} />
    </>
  );
}
